import type { PaymentMode, ShippingAddress } from '@dunea/shared';
import httpClient from '~/lib/http-client';

// Owner-facing Shop plugin API (studio `ShopOwnerController`).
// `appId` is the CHAT id — the shop is keyed by app and created the first time
// the owner opens it, so these calls work before the site is ever published.
// Money is integer minor units (paise, cents) everywhere.

export type ProductStatus = 'draft' | 'active' | 'archived';

export type OrderStatus =
  | 'pending'
  | 'paid'
  | 'fulfilled'
  | 'cancelled'
  | 'expired'
  | 'refunded'
  | 'partially_refunded'
  | 'disputed'
  | 'needs_refund';

export type DiscountType = 'percent' | 'fixed' | 'free_shipping';

export interface ShippingRule {
  amount: number;
  freeOver?: number | null;
}

export interface Shop {
  id: string;
  appId: string;
  name: string;
  currency: string;
  taxInclusive: boolean;
  skuPattern: string;
  lowStockThreshold: number;
  modules: string[];
  shipping: ShippingRule | null;
  notifyEmail: string | null;
}

export type ShopSettingsPatch = Partial<Omit<Shop, 'id' | 'appId'>>;

export interface Product {
  id: string;
  shopId: string;
  slug: string;
  title: string;
  description: string | null;
  productType: string | null;
  tags: string[];
  status: ProductStatus;
  seoTitle: string | null;
  seoDescription: string | null;
  hsnCode: string | null;
  taxRateBps: number | null;
  sort: number;
  createdAt: string;
  updatedAt: string;
}

export interface OptionSet {
  id: string;
  productId: string;
  position: number;
  name: string;
  values: string[];
}

export interface OptionSetInput {
  name: string;
  values: string[];
}

export interface Variant {
  id: string;
  shopId: string;
  productId: string;
  sku: string;
  barcode: string | null;
  options: string[];
  price: number;
  compareAtPrice: number | null;
  weightGrams: number | null;
  imageId: string | null;
  /** null = untracked (always available). */
  stock: number | null;
  /** Units held by open live checkouts. */
  reserved: number;
  allowBackorder: boolean;
  archivedAt: string | null;
}

export interface ProductImage {
  id: string;
  productId: string;
  url: string;
  alt: string | null;
  sort: number;
}

export interface ProductDetail {
  product: Product;
  optionSets: OptionSet[];
  variants: Variant[];
  images: ProductImage[];
}

export interface ProductInput {
  title: string;
  description?: string | null;
  tags?: string[];
  status?: ProductStatus;
  options?: OptionSetInput[];
  price: number;
  compareAtPrice?: number | null;
  stock?: number | null;
}

export type ProductPatch = Partial<
  Pick<
    Product,
    'title' | 'slug' | 'description' | 'productType' | 'tags' | 'status' | 'seoTitle' | 'seoDescription' | 'taxRateBps'
  >
>;

export interface VariantUpdate {
  price?: number;
  compareAtPrice?: number | null;
  sku?: string;
  barcode?: string | null;
  allowBackorder?: boolean;
  /** false stops tracking stock (the ledger is kept). */
  trackStock?: false;
}

export type StockChange = { set: number; note?: string } | { delta: number; note?: string };

export interface CollectionRule {
  tag?: string;
  productType?: string;
}

export interface Collection {
  id: string;
  shopId: string;
  slug: string;
  title: string;
  description: string | null;
  /** null = manual. */
  rule: CollectionRule | null;
  sort: number;
}

export interface CollectionInput {
  title: string;
  description?: string | null;
  rule?: CollectionRule | null;
  productIds?: string[];
}

export interface Discount {
  id: string;
  shopId: string;
  code: string;
  type: DiscountType;
  /** Basis points for percent, minor units for fixed, 0 for free shipping. */
  value: number;
  minSubtotal: number | null;
  usageLimit: number | null;
  usedCount: number;
  startsAt: string | null;
  endsAt: string | null;
  active: boolean;
}

export interface DiscountInput {
  code: string;
  type: DiscountType;
  value: number;
  minSubtotal?: number | null;
  usageLimit?: number | null;
  active?: boolean;
}

export interface Order {
  id: string;
  shopId: string;
  number: number;
  publicToken: string;
  status: OrderStatus;
  mode: PaymentMode;
  paymentId: string | null;
  currency: string;
  subtotal: number;
  shipping: number;
  discount: number;
  tax: number;
  total: number;
  buyerEmail: string | null;
  buyerName: string | null;
  shippingAddress: ShippingAddress | null;
  discountCode: string | null;
  trackingNumber: string | null;
  trackingUrl: string | null;
  note: string | null;
  holdUntil: string | null;
  paidAt: string | null;
  fulfilledAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OrderItem {
  id: string;
  orderId: string;
  variantId: string;
  sku: string;
  title: string;
  variantTitle: string | null;
  unitPrice: number;
  quantity: number;
  stockCommitted: boolean;
}

export interface OrderDetail {
  order: Order;
  items: OrderItem[];
}

const base = (appId: string) => `/api/plugins/shop/apps/${encodeURIComponent(appId)}`;
const seg = encodeURIComponent;

// ─── shop & settings ───

export async function apiGetShop(appId: string): Promise<Shop> {
  const { data } = await httpClient.get<Shop>(base(appId));
  return data;
}

export async function apiUpdateShopSettings(appId: string, patch: ShopSettingsPatch): Promise<Shop> {
  const { data } = await httpClient.put<Shop>(`${base(appId)}/settings`, patch);
  return data;
}

// ─── products ───

export async function apiListProducts(
  appId: string,
  filter: { q?: string; status?: ProductStatus[] } = {},
): Promise<ProductDetail[]> {
  const params = new URLSearchParams();

  if (filter.q?.trim()) {
    params.set('q', filter.q.trim());
  }

  if (filter.status?.length) {
    params.set('status', filter.status.join(','));
  }

  const query = params.toString();
  const { data } = await httpClient.get<ProductDetail[]>(`${base(appId)}/products${query ? `?${query}` : ''}`);

  return data;
}

export async function apiCreateProduct(appId: string, input: ProductInput): Promise<ProductDetail> {
  const { data } = await httpClient.post<ProductDetail>(`${base(appId)}/products`, input);
  return data;
}

/** Includes archived variants (combinations removed by an options edit). */
export async function apiGetProduct(appId: string, productId: string): Promise<ProductDetail> {
  const { data } = await httpClient.get<ProductDetail>(`${base(appId)}/products/${seg(productId)}`);
  return data;
}

export async function apiUpdateProduct(appId: string, productId: string, patch: ProductPatch): Promise<Product> {
  const { data } = await httpClient.patch<Product>(`${base(appId)}/products/${seg(productId)}`, patch);
  return data;
}

export async function apiSetProductOptions(
  appId: string,
  productId: string,
  body: { options: OptionSetInput[]; price?: number; stock?: number | null },
): Promise<ProductDetail & { created: Variant[]; archived: Variant[] }> {
  const { data } = await httpClient.put<ProductDetail & { created: Variant[]; archived: Variant[] }>(
    `${base(appId)}/products/${seg(productId)}/options`,
    body,
  );
  return data;
}

export async function apiAddProductImage(
  appId: string,
  productId: string,
  image: { url: string; alt?: string | null },
): Promise<ProductImage> {
  const { data } = await httpClient.post<ProductImage>(`${base(appId)}/products/${seg(productId)}/images`, image);
  return data;
}

export async function apiRemoveProductImage(appId: string, productId: string, imageId: string): Promise<void> {
  await httpClient.delete(`${base(appId)}/products/${seg(productId)}/images/${seg(imageId)}`);
}

// ─── variants & stock ───

export async function apiUpdateVariant(appId: string, variantId: string, update: VariantUpdate): Promise<Variant> {
  const { data } = await httpClient.patch<Variant>(`${base(appId)}/variants/${seg(variantId)}`, update);
  return data;
}

/** `{ set }` counts stock, `{ delta }` adjusts it. Either starts tracking an untracked variant. */
export async function apiChangeStock(appId: string, variantId: string, change: StockChange): Promise<Variant> {
  const { data } = await httpClient.post<Variant>(`${base(appId)}/variants/${seg(variantId)}/stock`, change);
  return data;
}

/** Tracked variants at or below the shop's low-stock threshold, lowest first. */
export async function apiLowStock(appId: string): Promise<Variant[]> {
  const { data } = await httpClient.get<Variant[]>(`${base(appId)}/low-stock`);
  return data;
}

// ─── collections & discounts ───

export async function apiListCollections(appId: string): Promise<Collection[]> {
  const { data } = await httpClient.get<Collection[]>(`${base(appId)}/collections`);
  return data;
}

export async function apiCreateCollection(appId: string, input: CollectionInput): Promise<Collection> {
  const { data } = await httpClient.post<Collection>(`${base(appId)}/collections`, input);
  return data;
}

export type CollectionPatch = Partial<CollectionInput> & { slug?: string };

export async function apiUpdateCollection(
  appId: string,
  collectionId: string,
  patch: CollectionPatch,
): Promise<Collection> {
  const { data } = await httpClient.patch<Collection>(`${base(appId)}/collections/${seg(collectionId)}`, patch);
  return data;
}

/** A manual collection's member product ids, in order (empty for rule collections). */
export async function apiCollectionProductIds(appId: string, collectionId: string): Promise<string[]> {
  const { data } = await httpClient.get<{ productIds: string[] }>(
    `${base(appId)}/collections/${seg(collectionId)}/products`,
  );
  return data.productIds;
}

export async function apiDeleteCollection(appId: string, collectionId: string): Promise<void> {
  await httpClient.delete(`${base(appId)}/collections/${seg(collectionId)}`);
}

export async function apiListDiscounts(appId: string): Promise<Discount[]> {
  const { data } = await httpClient.get<Discount[]>(`${base(appId)}/discounts`);
  return data;
}

/** Only never-used codes can be deleted (409 otherwise); deactivate used ones instead. */
export async function apiDeleteDiscount(appId: string, code: string): Promise<void> {
  await httpClient.delete(`${base(appId)}/discounts/${seg(code)}`);
}

export async function apiCreateDiscount(appId: string, input: DiscountInput): Promise<Discount> {
  const { data } = await httpClient.post<Discount>(`${base(appId)}/discounts`, input);
  return data;
}

export async function apiUpdateDiscount(
  appId: string,
  code: string,
  patch: Partial<Omit<DiscountInput, 'code' | 'type'>>,
): Promise<Discount> {
  const { data } = await httpClient.patch<Discount>(`${base(appId)}/discounts/${seg(code)}`, patch);
  return data;
}

// ─── orders ───

export async function apiListOrders(
  appId: string,
  filter: { status?: OrderStatus[]; mode?: PaymentMode; limit?: number } = {},
): Promise<Order[]> {
  const params = new URLSearchParams();

  if (filter.status?.length) {
    params.set('status', filter.status.join(','));
  }

  if (filter.mode) {
    params.set('mode', filter.mode);
  }

  if (filter.limit) {
    params.set('limit', String(filter.limit));
  }

  const query = params.toString();
  const { data } = await httpClient.get<Order[]>(`${base(appId)}/orders${query ? `?${query}` : ''}`);

  return data;
}

export async function apiGetOrder(appId: string, orderId: string): Promise<OrderDetail> {
  const { data } = await httpClient.get<OrderDetail>(`${base(appId)}/orders/${seg(orderId)}`);
  return data;
}

export async function apiFulfilOrder(
  appId: string,
  orderId: string,
  tracking: { trackingNumber?: string | null; trackingUrl?: string | null },
): Promise<Order> {
  const { data } = await httpClient.post<Order>(`${base(appId)}/orders/${seg(orderId)}/fulfil`, tracking);
  return data;
}

export async function apiCancelOrder(appId: string, orderId: string): Promise<Order> {
  const { data } = await httpClient.post<Order>(`${base(appId)}/orders/${seg(orderId)}/cancel`, {});
  return data;
}

/** Omit `amount` to refund what's left. */
export async function apiRefundOrder(appId: string, orderId: string, amount?: number): Promise<OrderDetail> {
  const { data } = await httpClient.post<OrderDetail>(
    `${base(appId)}/orders/${seg(orderId)}/refund`,
    amount === undefined ? {} : { amount },
  );
  return data;
}
